/* ──────────────────────────────────────────────────────────────
   Remote Battery Unlock — API helpers + response normalisers
   ────────────────────────────────────────────────────────────── */

export const REMOTE_SESSION_TAB = 'remote_session'
/** sessionStorage key used to open TicketDetail directly on a given tab */
export const TD_TAB_HANDOFF_KEY = 'crm_ticket_detail_initial_tab'
export const CNC_DASHBOARD_URL = 'https://sm-cnc-v3.azurewebsites.net/stationmonitoring'

/** stages in order; extra answer_lN keys from the API are appended automatically */
export const DEFAULT_STAGES = ['l1', 'l2', 'l3']

export const STAGE_META = {
  l1: { roman: 'I',   title: 'Stage I: Verification',   sub: 'Eligibility check', level: 'L1' },
  l2: { roman: 'II',  title: 'Stage II: Verification',  sub: 'Remote unlock',     level: 'L2' },
  l3: { roman: 'III', title: 'Stage III: Verification', sub: 'CNC review',        level: 'L3' },
}
export const stageMeta = (key) => {
  const n = String(key).replace(/\D/g, '')
  return STAGE_META[key] || { roman: n || '•', title: `Stage ${n || key}`, sub: '', level: String(key).toUpperCase() }
}
export const sortStageKeys = (keys) =>
  [...keys].sort((a, b) => (parseInt(a.replace(/\D/g, ''), 10) || 99) - (parseInt(b.replace(/\D/g, ''), 10) || 99))

export const slugify = (v) =>
  String(v || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')

export const isYesLabel = (l) => /^(yes|y|true)$/i.test(String(l || '').trim())
export const isNoLabel = (l) => /^(no|n|false)$/i.test(String(l || '').trim())

function pickText(obj, keys) {
  if (!obj || typeof obj !== 'object') return ''
  for (const k of keys) {
    const v = obj[k]
    if (typeof v === 'string' && v.trim()) return v
  }
  return ''
}
const asArray = (v) => (v == null ? [] : Array.isArray(v) ? v : [v])
const idOf = (v) => (v && typeof v === 'object' ? (v.id ?? v.option_id ?? v.question_id) : v)

/* ── access: profile.remote_session_level = ['l1', 'l2'] ── */
export function readRemoteSessionLevels(obj) {
  if (!obj || typeof obj !== 'object') return null
  const raw = obj.remote_session_level ?? obj.remote_session_levels ??
    obj.data?.remote_session_level ?? obj.profile?.remote_session_level ?? obj.user?.remote_session_level
  if (raw == null) return null
  return asArray(raw).map(x => String(x).trim().toLowerCase()).filter(Boolean)
}

/* ── GET get-checklist → [{ id, text, type, required, hint, options:[{id,label}], parentId, parentOptionIds }] ──
   Dependent questions are supported when the API sends either
     • question.parent_question / depends_on (+ parent_option / show_if_option …), or
     • option.child_questions / sub_questions / next_questions (nested or ids). */
export function normalizeChecklist(data) {
  let root = data?.data ?? data
  if (root?.checklist) root = root.checklist
  let qs = []
  if (Array.isArray(root)) qs = root
  else if (Array.isArray(root?.questions)) qs = root.questions
  else if (Array.isArray(root?.results)) qs = root.results
  if (qs.length && Array.isArray(qs[0]?.questions)) qs = qs.flatMap(c => c.questions || [])

  const out = []
  const links = {} // childId → { parentId, optionIds:Set }
  const link = (childId, parentId, optionId) => {
    const l = (links[childId] = links[childId] || { parentId, optionIds: new Set() })
    if (optionId != null) l.optionIds.add(String(optionId))
  }

  const visit = (q, i, parentId = null, parentOptionId = null) => {
    if (!q || typeof q !== 'object' || q.is_active === false) return
    const id = String(q.id ?? q.question_id ?? `q${out.length}`)
    const rawOpts = (q.options || q.choices || q.question_options || [])
      .filter(o => !(o && typeof o === 'object' && o.is_active === false))
      .sort((a, b) => Number(a?.sequence ?? 0) - Number(b?.sequence ?? 0))
    const options = rawOpts.map((o, j) => {
      if (typeof o !== 'object') return { id: String(o), label: String(o) }
      const optId = String(o.id ?? o.option_id ?? j)
      // option → child questions (nested objects or ids)
      const kids = o.child_questions || o.sub_questions || o.next_questions || o.questions ||
        (o.next_question != null ? [o.next_question] : [])
      asArray(kids).forEach((k, ki) => {
        if (k && typeof k === 'object') visit(k, ki, id, optId)
        else if (k != null) link(String(k), id, optId)
      })
      return {
        id: optId,
        label: pickText(o, ['option', 'label', 'name', 'text', 'value', 'title']) || `Option ${j + 1}`,
        slug: typeof o.slug === 'string' ? o.slug : '',
        // move_next: true → show the next question (by sequence) after this answer; false → form ends here
        moveNext: typeof o.move_next === 'boolean' ? o.move_next : null,
      }
    })
    const pId = parentId ?? idOf(q.parent_question ?? q.parent ?? q.depends_on ?? q.parent_question_id ?? q.depends_on_question)
    if (pId != null) {
      const pOpts = asArray(parentOptionId ?? q.parent_option ?? q.parent_option_id ?? q.depends_on_option ??
        q.show_if_option ?? q.trigger_option ?? q.parent_options ?? q.show_if_options)
      if (!pOpts.length) link(id, String(pId), null)
      pOpts.forEach(o => link(id, String(pId), idOf(o)))
    }
    out.push({
      id,
      text: pickText(q, ['question', 'question_text', 'text', 'title', 'name', 'label']) || `Question ${out.length + 1}`,
      // answer_type from get-checklist: 'radio' | 'dropdown' | 'text' …
      type: String(q.answer_type || q.question_type || q.type || (options.length ? 'radio' : 'text')).toLowerCase(),
      required: q.is_required ?? q.required ?? true,
      hint: pickText(q, ['help_text', 'hint']),
      order: Number(q.order ?? q.sequence ?? q.position ?? i),
      options,
    })
  }
  qs.forEach((q, i) => visit(q, i))

  // keep top-level order; children sit right after their parent
  const byId = Object.fromEntries(out.map(q => [q.id, q]))
  out.forEach(q => {
    const l = links[q.id]
    q.parentId = l && byId[l.parentId] ? l.parentId : null
    q.parentOptionIds = l ? [...l.optionIds] : []
  })
  const roots = out.filter(q => !q.parentId).sort((a, b) => a.order - b.order)
  const ordered = []
  const place = (q) => {
    ordered.push(q)
    out.filter(c => c.parentId === q.id).sort((a, b) => a.order - b.order).forEach(place)
  }
  roots.forEach(place)
  return ordered
}

/** questions currently visible for the given answers ({ [qid]: optionId | text }) */
export function visibleQuestions(questions, answers) {
  // One-by-one mode (get-checklist sends option.move_next): show questions in
  // sequence; after each answer, continue only if the chosen option has move_next = true.
  if (questions.some(q => q.options.some(o => o.moveNext !== null))) {
    const out = []
    for (const q of questions) {
      out.push(q)
      const a = answers[q.id]
      if (!String(a ?? '').trim()) break                 // wait for this answer
      if (q.options.length) {
        const opt = q.options.find(o => o.id === String(a))
        if (!opt || opt.moveNext === false) break       // this answer ends the form
      }
    }
    return out
  }
  const shown = new Set()
  return questions.filter(q => {
    if (!q.parentId) { shown.add(q.id); return true }
    if (!shown.has(q.parentId)) return false
    const a = answers[q.parentId]
    const ok = q.parentOptionIds.length ? q.parentOptionIds.includes(String(a)) : !!a
    if (ok) shown.add(q.id)
    return ok
  })
}

/* ── GET ticket-checklist-answer/<ticket_id>/
   { ticket_id, answer_l1: [{ question_id, question, answer_id, answer, metadata, description }], answer_l2: [], … }
   → { stages: ['l1','l2','l3'], byStage: { l1: [{ questionId, questionText, optionId, optionLabel, description }] } } */
export function normalizeTicketAnswers(data) {
  const root = data?.data ?? data ?? {}
  const byStage = {}
  const stages = new Set()
  Object.entries(root).forEach(([k, v]) => {
    const m = /^answer_(l\d+)$/i.exec(k)
    if (!m) return
    const stage = m[1].toLowerCase()
    stages.add(stage)
    byStage[stage] = asArray(v).map(a => {
      const q = a?.question
      const o = a?.option
      return {
        questionId: String(a?.question_id ?? idOf(q) ?? ''),
        questionText: typeof q === 'string' ? q : pickText(q, ['question', 'question_text', 'text', 'title']),
        optionId: a?.answer_id != null ? String(a.answer_id) : (o != null ? String(idOf(o)) : ''),
        optionLabel: typeof a?.answer === 'string' ? a.answer
          : (typeof o === 'object' && o ? pickText(o, ['option', 'label', 'name', 'text']) : ''),
        description: a?.description || '',
        metadata: a?.metadata || {},
      }
    })
  })
  const hasStageKeys = stages.size > 0
  DEFAULT_STAGES.forEach(s => stages.add(s))
  return { stages: sortStageKeys([...stages]), byStage, isChecklistTicket: hasStageKeys }
}

/* ── API calls ── */
export function fetchChecklist(fetchJson, stage, subCategorySlug) {
  const params = new URLSearchParams({ checklist_category: stage, sub_category_slug: subCategorySlug || '' })
  return fetchJson(`/api/checklists/checklists/get-checklist/?${params}`).then(normalizeChecklist)
}

export function fetchTicketAnswers(fetchJson, ticketId) {
  return fetchJson(`/api/tickets/ticket/ticket-checklist-answer/${ticketId}/`).then(normalizeTicketAnswers)
}

/** answers: [{ question, option, description? }] */
export function submitTicketAnswers(fetchJson, ticketId, stage, answers) {
  return fetchJson(`/api/tickets/ticket/ticket-checklist-answer/${ticketId}/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ checklist_stage: stage, answers }),
  })
}

/** questions + answers map → POST answers payload (only visible questions) */
export function buildAnswersPayload(questions, answers) {
  return visibleQuestions(questions, answers).map(q => (
    q.options.length
      ? { question: q.id, option: answers[q.id] }
      : { question: q.id, option: null, description: answers[q.id] }
  ))
}

/** PUT update-status (same route the header status dropdown uses) */
export function updateTicketStatus(fetchJson, ticketId, status) {
  return fetchJson(`/api/tickets/ticket/update-status/${ticketId}/`, {
    method: 'PUT',
    body: JSON.stringify({ status }),
  })
}

export const closeTicket = (fetchJson, ticketId) => updateTicketStatus(fetchJson, ticketId, 'closed')

/** GET assign-operator/<ticket_id>/ → hands the ticket to the station operator */
export function assignOperator(fetchJson, ticketId) {
  return fetchJson(`/api/tickets/ticket/assign-operator/${ticketId}/`)
}
